import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Activity } from "lucide-react";
import { CHIP_COLORS } from "../utils/colors";

// Generate realistic hourly data for 7 days (168 data points) for ~10,000 smart meters
const generateHourlyData = () => {
  const data = [];
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  // Seeded random for consistent but varied data
  const seededRandom = (seed: number) => {
    const x = Math.sin(seed * 9999) * 10000;
    return x - Math.floor(x);
  };

  // Smooth interpolation function
  const smoothValue = (base: number, seed: number, variance: number) => {
    return base + (seededRandom(seed) - 0.5) * variance;
  };

  for (let day = 0; day < 7; day++) {
    const isWeekend = day >= 5;
    const dayMultiplier = isWeekend ? 0.78 : 1.0;
    // Slight daily variation for weekdays
    const dailyOffset = isWeekend ? 0 : (seededRandom(day * 1000) - 0.5) * 300;

    for (let hour = 0; hour < 24; hour++) {
      const seed = day * 100 + hour;
      let consumption: number;

      // Define consumption based on time of day with smooth transitions
      if (hour >= 0 && hour <= 5) {
        // Nighttime lows: 3,500–4,500 kWh - gradual slight rise
        const progress = hour / 5;
        consumption = smoothValue(3500 + progress * 800, seed, 200);
      } else if (hour >= 6 && hour <= 9) {
        // Morning ramp-up: 4,500–9,500 kWh
        const progress = (hour - 6) / 3;
        consumption = smoothValue(4500 + progress * 5000, seed, 300);
      } else if (hour >= 10 && hour <= 14) {
        // Midday plateau with dip: 8,000–9,500 kWh
        let midBase = 9000;
        if (hour === 12) midBase = 8200;
        if (hour === 13) midBase = 8400;
        consumption = smoothValue(midBase, seed, 250);
      } else if (hour >= 15 && hour <= 17) {
        // Afternoon build-up: 9,000–10,500 kWh
        const progress = (hour - 15) / 2;
        consumption = smoothValue(9200 + progress * 1300, seed, 250);
      } else if (hour >= 18 && hour <= 20) {
        // Evening peak: 10,500–12,000 kWh
        let peakBase = 11200;
        if (hour === 19) peakBase = 11800;
        if (hour === 20) peakBase = 11400;
        consumption = smoothValue(peakBase, seed, 300);
      } else {
        // Late evening decline (21-23): 7,000–5,000 kWh
        const progress = (hour - 21) / 2;
        consumption = smoothValue(7000 - progress * 2000, seed, 300);
      }

      // Apply weekend reduction
      consumption = consumption * dayMultiplier + dailyOffset;

      // Clamp to realistic bounds
      consumption = Math.max(3200, Math.min(12200, consumption));

      data.push({
        time: `${days[day]} ${hour.toString().padStart(2, "0")}:00`,
        day: days[day],
        dayIndex: day,
        hour: hour,
        consumption: Math.round(consumption),
        fullTimestamp: `${days[day]}, ${hour.toString().padStart(2, "0")}:00`,
      });
    }
  }
  return data;
};

const data = generateHourlyData();

// Calculate summary stats
const totalConsumption = data.reduce((sum, d) => sum + d.consumption, 0);
const avgHourly = Math.round(totalConsumption / data.length);
const peakData = data.reduce(
  (max, d) => (d.consumption > max.consumption ? d : max),
  data[0],
);
const minData = data.reduce(
  (min, d) => (d.consumption < min.consumption ? d : min),
  data[0],
);

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const dataPoint = payload[0].payload;
    return (
      <div className="bg-card border border-border rounded-lg px-4 py-3 shadow-lg min-w-[160px]">
        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-border">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: CHIP_COLORS.info }}
          />
          <p className="text-sm font-semibold text-foreground">
            {dataPoint.fullTimestamp}
          </p>
        </div>
        <div className="space-y-1">
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Day:</span>
            <span className="text-xs font-medium text-foreground">
              {dataPoint.day}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Hour:</span>
            <span className="text-xs font-medium text-foreground">
              {dataPoint.hour.toString().padStart(2, "0")}:00
            </span>
          </div>
          <div className="flex justify-between items-center pt-1">
            <span className="text-xs text-muted-foreground">Consumption:</span>
            <span
              className="text-sm font-bold"
              style={{ color: CHIP_COLORS.info }}
            >
              {payload[0].value.toLocaleString()} kWh
            </span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const SystemConsumptionChart = () => {
  return (
    <div className="bg-card rounded-xl border border-border p-5">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5" style={{ color: CHIP_COLORS.info }} />
            <h3 className="font-heading font-semibold text-foreground">
              System Consumption by Hour (Last 7 Days)
            </h3>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            Aggregated kWh from ~10,000 smart meters • Endpoint AMI data
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Total (7 days)</p>
            <p className="text-lg font-semibold text-foreground">
              {(totalConsumption / 1000).toFixed(1)} MWh
            </p>
          </div>
          <div className="h-10 w-px bg-border" />
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Avg. Hourly</p>
            <p className="text-lg font-semibold text-foreground">
              {avgHourly.toLocaleString()} kWh
            </p>
          </div>
          <div className="h-10 w-px bg-border" />
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Peak Hour</p>
            <p
              className="text-lg font-semibold"
              style={{ color: CHIP_COLORS.info }}
            >
              {peakData.consumption.toLocaleString()} kWh
            </p>
            <p className="text-[10px] text-muted-foreground">
              {peakData.fullTimestamp}
            </p>
          </div>
          <div className="h-10 w-px bg-border" />
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Off-Peak Low</p>
            <p className="text-lg font-semibold text-muted-foreground">
              {minData.consumption.toLocaleString()} kWh
            </p>
            <p className="text-[10px] text-muted-foreground">
              {minData.fullTimestamp}
            </p>
          </div>
        </div>
      </div>

      <div className="h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
          >
            <defs>
              <linearGradient
                id="systemConsumptionGradient"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="0%"
                  stopColor={CHIP_COLORS.info}
                  stopOpacity={0.18}
                />
                <stop
                  offset="60%"
                  stopColor={CHIP_COLORS.info}
                  stopOpacity={0.06}
                />
                <stop
                  offset="100%"
                  stopColor={CHIP_COLORS.info}
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="hsl(var(--border))"
              strokeOpacity={0.3}
              horizontal={true}
              vertical={false}
            />
            <XAxis
              dataKey="time"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 9 }}
              interval={5}
              angle={-45}
              textAnchor="end"
              height={50}
              tickFormatter={(value, index) => {
                const [day, time] = value.split(" ");
                const hour = parseInt(time);
                // Show day name at midnight, then 06:00, 12:00, 18:00
                if (hour === 0) return day;
                if (hour === 6 || hour === 12 || hour === 18) return time;
                return "";
              }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
              tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
              domain={[2000, 13000]}
              ticks={[3000, 5000, 7000, 9000, 11000, 13000]}
              width={40}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ stroke: "hsl(var(--border))", strokeWidth: 1 }}
            />
            <Area
              type="monotone"
              dataKey="consumption"
              stroke={CHIP_COLORS.info}
              strokeWidth={1.5}
              fill="url(#systemConsumptionGradient)"
              dot={false}
              activeDot={{
                r: 5,
                fill: CHIP_COLORS.info,
                stroke: "hsl(var(--background))",
                strokeWidth: 2,
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Day indicator bar */}
      <div className="flex mt-2 border-t border-border pt-3">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day, index) => (
          <div
            key={day}
            className={`flex-1 text-center border-r last:border-r-0 border-border ${
              index >= 5 ? "bg-muted/30" : ""
            }`}
          >
            <span
              className={`text-xs font-medium ${
                index >= 5 ? "text-muted-foreground" : "text-foreground"
              }`}
            >
              {day}
            </span>
            {index >= 5 && (
              <span className="block text-[9px] text-muted-foreground">
                Weekend
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SystemConsumptionChart;
